import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, SITE, waLink, DEFAULT_WA_MSG } from "../lib/site";
import { WaIcon } from "./icons";
import { useEnquiry } from "./EnquiryModal";

export const Footer = () => {
  const { openEnquiry } = useEnquiry();
  return (
    <footer className="relative overflow-hidden bg-[#141517] pb-28 text-ivory md:pb-0">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-6 left-0 w-full select-none text-center font-display text-[20vw] font-semibold leading-none text-transparent"
        style={{ WebkitTextStroke: "1px rgba(253,251,247,0.06)" }}
      >
        PARAMOUNT
      </div>

      <div className="relative mx-auto max-w-7xl px-6 pt-24">
        <div className="flex flex-col items-start justify-between gap-8 border-b border-white/10 pb-14 lg:flex-row lg:items-end">
          <h2 className="text-balance max-w-2xl font-display text-4xl font-medium leading-tight md:text-5xl">
            Let&rsquo;s build a kitchen that is
            <span className="serif-accent text-gold"> built to last.</span>
          </h2>
          <div className="flex flex-wrap gap-4">
            <button
              data-testid="footer-design-my-kitchen"
              onClick={() => openEnquiry({ source: "footer" })}
              className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-4 text-sm font-semibold text-royal transition-all duration-300 hover:-translate-y-0.5 hover:bg-golddim hover:text-ivory"
            >
              Design My Kitchen <ArrowUpRight className="h-4 w-4" />
            </button>
            <a
              data-testid="footer-whatsapp"
              href={waLink(DEFAULT_WA_MSG)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 text-sm font-semibold text-ivory transition-colors hover:bg-white/10"
            >
              <WaIcon className="h-4 w-4" /> WhatsApp Us
            </a>
          </div>
        </div>

        <div className="grid gap-12 py-14 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="Paramount Kitchen logo" className="h-11 w-11" />
              <div className="leading-none">
                <p className="font-display text-lg font-semibold">PARAMOUNT</p>
                <p className="mt-1 text-[9px] font-semibold uppercase tracking-[0.42em] text-gold">Kitchen</p>
              </div>
            </div>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-ivory/60">
              Premium modular kitchens manufactured with engineered carcass construction,
              carefully selected materials and precision manufacturing.
            </p>
            <p className="mt-5 flex items-start gap-2 text-sm text-ivory/60">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {SITE.location}
            </p>
          </div>

          <div>
            <p className="eyebrow text-gold">Explore</p>
            <ul className="mt-5 space-y-3 text-sm">
              {NAV_LINKS.slice(0, 4).map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-ivory/70 transition-colors hover:text-gold">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="eyebrow text-gold">&nbsp;</p>
            <ul className="mt-5 space-y-3 text-sm">
              {NAV_LINKS.slice(4).map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-ivory/70 transition-colors hover:text-gold">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="eyebrow text-gold">Contact</p>
            <ul className="mt-5 space-y-3 text-sm text-ivory/70">
              <li>
                <a href="tel:+916230492888" data-testid="footer-phone" className="flex items-center gap-3 transition-colors hover:text-gold">
                  <Phone className="h-4 w-4 text-gold" /> {SITE.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={`mailto:${SITE.email}`} data-testid="footer-email" className="flex items-center gap-3 break-all transition-colors hover:text-gold">
                  <Mail className="h-4 w-4 shrink-0 text-gold" /> {SITE.email}
                </a>
              </li>
              <li>
                <a href={waLink(DEFAULT_WA_MSG)} target="_blank" rel="noreferrer" className="flex items-center gap-3 transition-colors hover:text-gold">
                  <WaIcon className="h-4 w-4 text-gold" /> WhatsApp {SITE.phoneDisplay}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-white/10 py-7 text-xs text-ivory/45 md:flex-row">
          <p>© {new Date().getFullYear()} Paramount Kitchen. All rights reserved.</p>
          <p>Designed, engineered &amp; manufactured in Himachal Pradesh, India.</p>
        </div>
      </div>
    </footer>
  );
};
